import { useEffect, useMemo, useState } from "react"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { Autocomplete, Box, Button, FormControl, Grid, InputLabel, LinearProgress, MenuItem, Select, SelectChangeEvent, SelectProps, TextField } from "@mui/material"
import useAuth from "../context/AuthProvider"
import { ClientBillingReportModel, ClientBillingReportModel_Resp } from "../models/ClientBillingReportModel"
import { ClientBillingReportMonthlyList } from "../components/Lists/ClientBillingReportMonthlyList"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import React from "react"
import Swal from "sweetalert2"
import { apiClient } from "../helper/api"
import { Close, Search, Refresh, Clear } from "@mui/icons-material"
import {
  billingCompanyRollupForApi,
  billingPartnerIdForApi,
  billingSelectionFromSelectValue,
  buildPartnerBillingAutocompleteOptions,
  findPartnerBillingAutocompleteOption,
  type PartnerBillingAutocompleteOption,
} from "../helper/partnerBillingSelect"
import { initPartner, PartnerHierarchyItem, PartnerType } from "../models/PartnersModel"

const keyNames: (keyof ClientBillingReportModel)[] = [
  'stateId', 'reportName', 'period', 'license', 'dateFinal', 'isPaymentRequired', 'paymentType', 'isReportRequired', 'licenseMain', 'premiumReport', 'feeDue'
]

export const MonthEndBillingModule = () => {

  const { user } = useAuth()
  const token = user?.token
  const [loading, setloading] = useState(false)

  const initValue: ClientBillingReportModel_Resp = {
    items: [],
    totalCount: 0,
    pageCount: 0,
    pageSize: 25,
    currentPage: 0,
  }
  const [itemList, setItemList] = useState<ClientBillingReportModel[]>(initValue.items)
  const [currentPage, setCurrentPage] = useState(initValue.currentPage)
  const [pageSize, setPageSize] = useState(initValue.pageSize)
  
  const combineFilters = (filterModel: any) => {
    if (!filterModel?.items || filterModel.items.length === 0) {
      return { filters: [], firstFilter: { fieldName: '', value: '', operator: '' } }
    }
  
    // Get ALL active filters
    const activeFilters = filterModel.items.filter((item: any) => 
      item.columnField && 
      item.columnField.trim() !== '' && 
      item.value !== undefined && 
      item.value !== null && 
      item.value.toString().trim() !== '' &&
      item.operatorValue && 
      item.operatorValue.trim() !== ''
    );
  
    if (activeFilters.length === 0) {
      return { filters: [], firstFilter: { fieldName: '', value: '', operator: '' } }
    }
  
    console.log('Using filters:', activeFilters); // DEBUG
  
    const processedFilters = activeFilters.map((filter: any) => ({
      fieldName: filter.columnField || '',
      value: filter.value.toString() || '',
      operator: filter.operatorValue || ''
    }));

    return {
      filters: processedFilters,
      firstFilter: processedFilters[0] || { fieldName: '', value: '', operator: '' }
    };
  }

  const [billingSelectValue, setBillingSelectValue] = useState<number | string>(0)

  const fetchData = (
    action: string,
    pageSize: number,
    currentPage: number,
    period: string,
    PartnerId: string,
    month: string,
    year: string,
    filterModel: any,
    field: string,
    sort: string,
    /** When set (e.g. dropdown `raw` before setState), avoids stale companyRollup. Omit to use current billingSelectValue. */
    billingRollupSelectValue?: number | string
  ) => {
    setloading(true);
    const params = new URLSearchParams();

     // Combine multiple filters
  const combinedFilters = combineFilters(filterModel)
  console.log('Sending filters:', combinedFilters); // DEBUG

    params.append('pageSize', pageSize.toString() || '');
    params.append('currentPage', currentPage.toString() || '');
    params.append('period', period || '');
    params.append('partnerId', PartnerId || '');
    if (period === "Monthly") {
      params.append('month', month || '1');
    } else {
      params.append('month', '0');
    }
    params.append('year', year || '');
    
    // Handle multiple filters
    if (combinedFilters.filters.length > 0) {
      // Send all filters as JSON in the filters parameter
      params.append('filters', JSON.stringify(combinedFilters.filters));
      
      // For backward compatibility, also send the first filter as individual params
      params.append('fieldName', combinedFilters.firstFilter.fieldName);
      params.append('value', combinedFilters.firstFilter.value);
      params.append('operator', combinedFilters.firstFilter.operator);
    } else {
      params.append('fieldName', '');
      params.append('value', '');
      params.append('operator', '');
    }
    
    params.append('field', field || '');
    params.append('sort', sort || '');
    const rollupSource =
      billingRollupSelectValue !== undefined ? billingRollupSelectValue : billingSelectValue;
    params.append('companyRollup', String(billingCompanyRollupForApi(rollupSource)));

    apiClient(`/MonthEndBilling/get-month-end-billing?${params.toString()}`, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    }).then(responseJson => {
      console.log('API Response:', responseJson); // DEBUG
      if (!responseJson.data?.items?.length) {
        // Do something if no items.
        setItemList([])
        return
      }
      setItemList(responseJson.data.items)
    }).finally(() => {
      setloading(false)
    });
  };
  const [selectedValue, setSelectedValue] = useState<string>("Monthly");

  const [selectMonthValue, setSelectedMonthValue] = useState<string>("1");
  const [sortModel, setSortModel] = useState([{ field: "", sort: "" }]);
  const [filterModel, setFilterModel] = useState({ items: [{ columnField: "", value: "", operatorValue: "" }] });

  const [yearValue, setYearValue] = useState(new Date().getFullYear().toString());

  const [partnersHierarchy, setPartnersHierarchy] = useState<PartnerHierarchyItem[]>([]);
  const fetchCustomer = () => {
    apiClient("/Partners/getPartnersHierarchy", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return
        setPartnersHierarchy(responseJson.data || []);
      })
  };

  const [selPartner, setSelPartner] = useState<PartnerType>(initPartner)
  const billingPartnerIdApi = useMemo(
    () => billingPartnerIdForApi(billingSelectValue, partnersHierarchy),
    [billingSelectValue, partnersHierarchy]
  )

  const deleteOTRData = async (items: ClientBillingReportModel[]) => {
    const payload = {
      clientBillingReports: items.map(item => ({
        CustomerPolicyId: item.customerPolicyId,    // uppercase key
        PolicyStatus: item.filed       // match your new validator key
      }))
    };
    // Send the array of items in one request
    const response = await apiClient(`/MonthEndBilling/delete-Client-Billing-Report`, {
      method: "POST",
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
      // body: JSON.stringify(items), // Send the array of items
      body: JSON.stringify(payload),
    });

    if (!response) return
    console.log("Delete response:", response);
    setItemList([]);
    // reloadData();
    return response;
  };

  const getCBR = async () => {
    const partnerId = billingPartnerIdForApi(billingSelectValue, partnersHierarchy);
    fetchData(
      'getMonthEndBilling',
      50,
      1,
      'Monthly',
      partnerId,
      selectMonthValue,
      yearValue,
      filterModel,
      sortModel?.[0]?.field ?? '',
      sortModel?.[0]?.sort ?? '',
      billingSelectValue
    );
  }

  useEffect(() => {
    getCBR()
    fetchCustomer()
  }, [])

  const handlePageChange = (val: number) => {
    setCurrentPage(val)
  }
  const handlePageSizeChange = (val: number) => {
    if (val <= 0) return
    setPageSize(val)
  }

  const handleRemoveItems = async (values: ClientBillingReportModel[]) => {
    console.log({ values })
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      iconHtml: '<svg width="70" height="70" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="31" cy="31" r="31" fill="#02A4E3"/><path d="M31.0003 25.6667V31M31.0003 36.3334H31.0137M44.3337 31C44.3337 38.3638 38.3641 44.3334 31.0003 44.3334C23.6365 44.3334 17.667 38.3638 17.667 31C17.667 23.6362 23.6365 17.6667 31.0003 17.6667C38.3641 17.6667 44.3337 23.6362 44.3337 31Z" stroke="white" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      showCancelButton: true,
      confirmButtonColor: '#02A4E3',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Delete',
      customClass: {
        confirmButton: 'btn-radius',
        cancelButton: 'btn-radius',
        popup: 'card-radius'
      }
    })

    if (result.isConfirmed) {
      await deleteOTRData(values)
      getCBR()
    }
  }

  const handleChange = (event: SelectChangeEvent<string>) => {

    setSelectedValue(event.target.value);
    fetchData("getMonthEndBilling", 50, 1, event.target.value, billingPartnerIdApi, selectMonthValue, yearValue, filterModel, sortModel?.[0]?.field ?? '', sortModel?.[0]?.sort ?? '');

  };

  const handleMonthChange = (event: SelectChangeEvent<string>) => {

    setSelectedMonthValue(event.target.value);
    fetchData("getMonthEndBilling", 50, 1, selectedValue, billingPartnerIdApi, event.target.value, yearValue, filterModel, sortModel?.[0]?.field ?? '', sortModel?.[0]?.sort ?? '');
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    setYearValue(event.target.value);
    const currentYear = new Date().getFullYear();
    let value = parseInt(event.target.value, 10);

    if (value > 1900 && value <= currentYear) {
      fetchData("getMonthEndBilling", 50, 1, selectedValue, billingPartnerIdApi, selectMonthValue, event.target.value, filterModel, sortModel?.[0]?.field ?? '', sortModel?.[0]?.sort ?? '');
    }
  };


  const handleSortChange = (newSortModel: any) => {
    setSortModel(newSortModel)
    console.log('filterModel', filterModel);
    console.log('newSortModel', newSortModel);
    fetchData("getMonthEndBilling", 50, 1, selectedValue, billingPartnerIdApi, selectMonthValue, yearValue, filterModel, newSortModel?.[0]?.field ?? '', newSortModel?.[0]?.sort ?? '');
  };

  const handleFilterChange = (newFilterModel: any) => {
    setFilterModel(newFilterModel)
    fetchData("getMonthEndBilling", 50, 1, selectedValue, billingPartnerIdApi, selectMonthValue, yearValue, newFilterModel, sortModel?.[0]?.field ?? '', sortModel?.[0]?.sort ?? '');
  };

  const billingAutocompleteOptions = useMemo(
    () => buildPartnerBillingAutocompleteOptions(partnersHierarchy),
    [partnersHierarchy]
  )

  const FMSelectCustomCustomer: React.FC<SelectProps> = ({ variant, ...props }) => {
    const handlePartnerAutocompleteChange = (
      _event: React.SyntheticEvent,
      option: PartnerBillingAutocompleteOption | null
    ) => {
      const raw = option?.selectValue ?? "0"
      const apiId = billingPartnerIdForApi(raw, partnersHierarchy)
      setBillingSelectValue(raw)
      setSelPartner(billingSelectionFromSelectValue(raw, partnersHierarchy))
      fetchData(
        "getMonthEndBilling",
        50,
        1,
        selectedValue,
        apiId,
        selectMonthValue,
        yearValue,
        filterModel,
        sortModel?.[0]?.field ?? "",
        sortModel?.[0]?.sort ?? "",
        raw
      )
    }

    const autocompleteValue = findPartnerBillingAutocompleteOption(
      billingAutocompleteOptions,
      billingSelectValue
    )

    return (
      <>
        <FormControl fullWidth>
          <Autocomplete
            id="test-select-label"
            className="border-round custom-menu-item"
            options={billingAutocompleteOptions}
            popupIcon={<ExpandMoreIcon />}
            isOptionEqualToValue={(a, b) =>
              String(a.selectValue) === String(b.selectValue)
            }
            getOptionLabel={(option) => option.label}
            value={autocompleteValue ?? undefined}
            onChange={handlePartnerAutocompleteChange}
            disableClearable
            renderOption={(liProps, option) => {
              const { key, ...rest } = liProps as typeof liProps & {
                key?: string
              }
              return (
                <Box
                  component="li"
                  key={key}
                  {...rest}
                  sx={
                    option.isCompanyRow
                      ? (theme) => ({
                          py: 1,
                          pl: 1.5,
                          pr: 2,
                          lineHeight: 1.35,
                          fontSize: "0.9375rem",
                          fontWeight: 700,
                          letterSpacing: "0.01em",
                          color: "text.primary",
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? theme.palette.grey[800]
                              : theme.palette.grey[200],
                          borderLeft: `3px solid ${theme.palette.primary.main}`,
                          "&.Mui-focused, &.Mui-focusVisible": {
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[700]
                                : theme.palette.grey[300],
                          },
                        })
                      : {
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          pl: 2,
                          py: 0.75,
                          whiteSpace: "normal",
                        }
                  }
                >
                  {option.treePrefix != null && option.treePrefix !== "" ? (
                    <>
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          flexShrink: 0,
                          width: "2em",
                          minWidth: "2em",
                          justifyContent: "flex-end",
                          pr: 0.25,
                          fontFamily:
                            'ui-monospace, "Cascadia Code", "Segoe UI Mono", Consolas, monospace',
                          fontSize: "0.8125rem",
                          color: "text.secondary",
                          lineHeight: 1.5,
                          userSelect: "none",
                        }}
                        aria-hidden
                      >
                        {option.treePrefix}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          lineHeight: 1.5,
                          pl: 0.25,
                        }}
                      >
                        {option.label}
                      </Box>
                    </>
                  ) : (
                    option.label
                  )}
                </Box>
              )
            }}
            sx={{
              marginTop: "-4px !important",
              "& .MuiAutocomplete-input": {
                padding: "0px 4px 0px 5px !important",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client Name"
                size="small"
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink:
                    parseInt(billingSelectValue?.toString() ?? "0", 10) > 0 ||
                    billingSelectValue === "0" ||
                    (typeof billingSelectValue === "string" &&
                      billingSelectValue.startsWith("__co_")) ||
                    (typeof params.inputProps.value === "string" &&
                      params.inputProps.value.length > 0),
                }}
                sx={{
                  ".MuiFormLabel-root.Mui-focused": {
                    color:
                      typeof params.inputProps.value === "string" &&
                      params.inputProps.value.length > 0
                        ? "#000"
                        : "#cccccc !important",
                  },
                  "& .MuiInputBase-input": { height: "25px" },
                  "& .MuiInputBase-root": { marginTop: "5px !important" },
                  "& .MuiOutlinedInput-root": { padding: "4.5px 9px" },
                  "& .MuiFormLabel-root": {
                    transform: "translate(14px, 13px) scale(1)",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -2px) scale(0.75)",
                  },
                }}
              />
            )}
          />
        </FormControl>
      </>
    )
  }

  const [formPartner, setFormPartner] = useState<PartnerType>(initPartner);
  const [formMonth, setFormMonth] = useState<string>("1");
  const [formYear, setFormYear] = useState<string>(new Date().getFullYear().toString());

  const ClearSearch = () => {
    setBillingSelectValue('0');
    setSelectedMonthValue('1');
    setSelectedValue('Monthly');
    setYearValue(new Date().getFullYear().toString());
    // setFilterModel({ items: [] });
    // setSortModel([{ field: "", sort: "" }]);
    setCurrentPage(1);
    getCBR();
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#fafbfc',
          minHeight: 56,
          // mb: 3,
          marginLeft: { xs: '-24px', lg: '0px' },
          marginRight: { xs: '-24px', lg: '0px' },
        }}
      >
        <h4 className="mb-0 py-2" style={{ fontWeight: 600, color: "#0f172a" }}>
          Month End Billing Report
        </h4>
      </Box>
      <Box
        sx={{
          width: '100%',
          py: 2,
          px: 2,
          // backgroundColor: '#ffffff',
          // borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Grid
            container
            spacing={2}
            // alignItems="center"
            className="filter-grid"
            display="flex"
          >
            {user?.userRoles.split(",").includes("Super Admin") && (
              <Grid item xl={2} lg={4} sm={6} xs={12}>
                <FMSelectCustomCustomer
                  name="customer"
                  // formValue={formPartner}
                  // onFormChange={(v) => setFormPartner(v ?? initPartner)}
                />
              </Grid>
            )}
          
          {/* </div> */}
          <Grid item xl={2} lg={4} sm={6} xs={12}>
          <FormControl fullWidth>
            <InputLabel className="" id="month-Label">Select Month</InputLabel>
            <Select
              labelId="month-Label"
              IconComponent={ExpandMoreIcon}
              label="Select Month"
              value={selectMonthValue}
              onChange={handleMonthChange}
              size="small"
              sx={{
                // marginTop: "-1px !important",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                // "& .MuiInputBase-root": { marginTop: "-3px !important" },
                "& .MuiInputBase-input": { padding: "7px 14px !important" },
              }}
            >
              <MenuItem value={1}>January</MenuItem>
              <MenuItem value={2}>February</MenuItem>
              <MenuItem value={3}>March</MenuItem>
              <MenuItem value={4}>April</MenuItem>
              <MenuItem value={5}>May</MenuItem>
              <MenuItem value={6}>June</MenuItem>
              <MenuItem value={7}>July</MenuItem>
              <MenuItem value={8}>August</MenuItem>
              <MenuItem value={9}>September</MenuItem>
              <MenuItem value={10}>October</MenuItem>
              <MenuItem value={11}>November</MenuItem>
              <MenuItem value={12}>December</MenuItem>
            </Select>
          </FormControl>
          </Grid>
          <Grid item xl={2} lg={4} sm={6} xs={12}>
            <FormControl fullWidth>
              <TextField
                label="Year"
                type="number"
                value={yearValue}
                onChange={handleYearChange}
                defaultValue={new Date().getFullYear()}
                // InputProps={{
                //   style: { height: '2.8rem' } // Change font size here for input text
                // }}
                size="small"
                fullWidth
                variant="outlined"
                sx={{
                  // "& .MuiInputBase-formControl ": {marginTop: "-1px !important"},
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                  // "& .MuiInputBase-root": { marginTop: "-3px !important" },
                  "& .MuiInputBase-input": { padding: "4.5px 14px !important" },
                }}
              />
            </FormControl>
          </Grid>
        </Grid>

      </Box>
      {/* </div> */}
      <Box
        // overflow={"scroll"}

        sx={{
          height: "calc(100% - 60px)",
          width: "100%"
        }}
      >
        <ClientBillingReportMonthlyList
          dataLoading={loading}
          sortModel={sortModel}
          setSortModel={setSortModel}
          listData={itemList}
          onRemoveItems={handleRemoveItems}
          setItemList={setItemList}
          selectedMonth={Number(selectMonthValue)}
          handleFilterChange={handleFilterChange}
          handleSortChange={handleSortChange}
          selectedPeriod={selectedValue}
          selectedYear={yearValue}
          selectedPartner={selPartner}
          billingCompanyRollup={billingCompanyRollupForApi(billingSelectValue)}
        />
      </Box>
    </LocalizationProvider>
  )
}